/**
 * CostAnalysisGrid - 2x2 grid layout for cost and token analysis
 *
 * Layout:
 * ┌──────────────────────────┬──────────────────────────┐
 * │ Cost by Model            │ Tokens by Agent          │
 * │ (dropdown: cost/tokens)  │ (dropdown: cost/tokens)  │
 * ├──────────────────────────┼──────────────────────────┤
 * │ Cost/Token History       │ Agent Token History      │
 * │ (dropdown: cost/tokens)  │ (dropdown: cost/tokens)  │
 * └──────────────────────────┴──────────────────────────┘
 *
 * Features:
 * - 4 independent charts with equal heights/widths per row/column
 * - All dropdowns support Cost + Token types
 * - Dropdown selections persist to localStorage independently
 * - Each chart has its own card border/background
 * - Charts handle their own empty states
 */

import React, { useState, useEffect, useMemo } from 'react';
import { ChevronDown, DollarSign, Hash, FileText, PenLine, Database } from 'lucide-react';
import { palette, typography, components } from '../../utils/designTokens';
import ModelCostOverTimeChart from './ModelCostOverTimeChart';
import CostByModelChart from './CostByModelChart';
import CostByAgentChart from './CostByAgentChart';
import AgentCostOverTimeChart from './AgentCostOverTimeChart';

// Shared metric options for all charts: Cost + Token types
const METRIC_OPTIONS = [
  { value: 'cost_usd', label: 'Cost', icon: DollarSign, unit: 'USD' },
  { value: 'total_tokens', label: 'Total Tokens', icon: Hash, unit: 'tokens' },
  { value: 'prompt_tokens', label: 'Prompt Tokens', icon: FileText, unit: 'tokens' },
  { value: 'completion_tokens', label: 'Completion Tokens', icon: PenLine, unit: 'tokens' },
  { value: 'cached_tokens', label: 'Cached Tokens', icon: Database, unit: 'tokens' },
];

const CostAnalysisGrid = ({ modelCostOverTimeData, agentCostOverTimeData, timeWindow, appId, environment }) => {

  // Helper to load persisted metric from localStorage
  const loadPersistedMetric = (key, defaultValue) => {
    try {
      return localStorage.getItem(`app_${appId}_${key}`) || defaultValue;
    } catch {
      return defaultValue;
    }
  };

  // State for selected metrics (dropdown open/close managed internally by TokenDropdown)
  const [selectedAggregateMetric, setSelectedAggregateMetric] = useState(() => loadPersistedMetric('aggregateMetric', 'cost_usd'));
  const [selectedTimeMetric, setSelectedTimeMetric] = useState(() => loadPersistedMetric('timeMetric', 'cost_usd'));
  const [selectedModelToken, setSelectedModelToken] = useState(() => loadPersistedMetric('modelTokenMetric', 'total_tokens'));
  const [selectedTimeToken, setSelectedTimeToken] = useState(() => loadPersistedMetric('timeTokenMetric', 'total_tokens'));

  // Persist metric selections to localStorage
  useEffect(() => {
    try {
      localStorage.setItem(`app_${appId}_aggregateMetric`, selectedAggregateMetric);
      localStorage.setItem(`app_${appId}_timeMetric`, selectedTimeMetric);
      localStorage.setItem(`app_${appId}_modelTokenMetric`, selectedModelToken);
      localStorage.setItem(`app_${appId}_timeTokenMetric`, selectedTimeToken);
    } catch (e) {
      console.error('Failed to save metrics to localStorage:', e);
    }
  }, [selectedAggregateMetric, selectedTimeMetric, selectedModelToken, selectedTimeToken, appId]);

  // Memoize option lookups
  const selectedAggregateMetricOption = useMemo(() =>
    METRIC_OPTIONS.find(opt => opt.value === selectedAggregateMetric),
    [selectedAggregateMetric]
  );

  const selectedTimeMetricOption = useMemo(() =>
    METRIC_OPTIONS.find(opt => opt.value === selectedTimeMetric),
    [selectedTimeMetric]
  );

  const selectedModelTokenOption = useMemo(() =>
    METRIC_OPTIONS.find(opt => opt.value === selectedModelToken),
    [selectedModelToken]
  );

  const selectedTimeTokenOption = useMemo(() =>
    METRIC_OPTIONS.find(opt => opt.value === selectedTimeToken),
    [selectedTimeToken]
  );

  // Chart Card Component (reusable for each of the 4 charts)
  const ChartCard = ({ children }) => (
    <div
      className={`${components.card.bg} ${components.card.rounded} ${components.card.padding} flex flex-col`}
      style={{
        border: `1px solid ${components.card.borderColor}`,
        minHeight: '352px',
      }}
    >
      {children}
    </div>
  );

  // Token Dropdown Component - manages its own open/close state to avoid parent re-renders
  const TokenDropdown = ({ selectedOption, options, onSelect }) => {
    const [isOpen, setIsOpen] = useState(false);

    return (
      <div className="relative">
        <button
          onClick={(e) => {
            e.stopPropagation();
            setIsOpen(!isOpen);
          }}
          className="border border-border-subtle rounded-full px-3 py-1.5 text-sm text-text-primary bg-transparent hover:bg-accent-secondary-base/10 transition-colors flex items-center gap-2 focus:outline-none focus:ring-2 focus:ring-accent-lavender"
          style={{ minWidth: '180px' }}
        >
        {selectedOption?.icon && (
          <selectedOption.icon size={16} style={{ color: palette.text.muted }} />
        )}
        <span className="flex-1 text-left">{selectedOption?.label || 'Total Tokens'}</span>
        <ChevronDown size={12} className={`text-text-muted transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div
          className="absolute top-full right-0 mt-2 rounded-lg overflow-hidden z-10"
          style={{
            backgroundColor: components.dropdown.container.background,
            border: components.dropdown.container.border,
            boxShadow: components.dropdown.container.shadow,
            minWidth: '200px',
          }}
        >
          {options.map((option) => {
            const Icon = option.icon;
            return (
              <button
                key={option.value}
                onClick={() => {
                  setIsOpen(false);
                  onSelect(option.value);
                }}
                className={`w-full text-left px-4 py-2.5 ${typography.size.sm} transition-colors flex items-center gap-3`}
                style={{
                  color: option.value === selectedOption?.value
                    ? components.dropdown.item.color.selected
                    : components.dropdown.item.color.default,
                  backgroundColor: option.value === selectedOption?.value
                    ? components.dropdown.item.hover.background
                    : 'transparent',
                }}
                onMouseEnter={(e) => {
                  if (option.value !== selectedOption?.value) {
                    e.currentTarget.style.backgroundColor = components.dropdown.item.hover.background;
                  }
                }}
                onMouseLeave={(e) => {
                  if (option.value !== selectedOption?.value) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
              >
                <Icon size={16} style={{ color: palette.text.muted, flexShrink: 0 }} />
                <span style={{ whiteSpace: 'nowrap' }}>{option.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
    );
  };

  return (
    <div
      className="grid grid-cols-1 xl:grid-cols-2"
      style={{
        gap: '16px',
        width: '100%',
      }}
    >
      {/* Top-left: Model Aggregates */}
      <ChartCard>
        <div className="flex items-start justify-between mb-4" style={{ minHeight: '40px' }}>
          <h3 className={`${typography.size.xs} ${typography.weight.semibold} uppercase tracking-wide`} style={{ color: palette.text.neutral }}>
            Model Aggregates
          </h3>
          <TokenDropdown
            selectedOption={selectedAggregateMetricOption}
            options={METRIC_OPTIONS}
            onSelect={setSelectedAggregateMetric}
          />
        </div>
        <div style={{ height: '280px', width: '100%' }}>
          <CostByModelChart
            data={modelCostOverTimeData}
            selectedMetric={selectedAggregateMetric}
            timeWindow={timeWindow}
            appId={appId}
            environment={environment}
          />
        </div>
      </ChartCard>

      {/* Top-right: Agent Aggregates */}
      <ChartCard>
        <div className="flex items-start justify-between mb-4" style={{ minHeight: '40px' }}>
          <h3 className={`${typography.size.xs} ${typography.weight.semibold} uppercase tracking-wide`} style={{ color: palette.text.neutral }}>
            Agent Aggregates
          </h3>
          <TokenDropdown
            selectedOption={selectedModelTokenOption}
            options={METRIC_OPTIONS}
            onSelect={setSelectedModelToken}
          />
        </div>
        <div style={{ height: '280px', width: '100%' }}>
          <CostByAgentChart
            data={agentCostOverTimeData}
            selectedMetric={selectedModelToken}
            timeWindow={timeWindow}
            appId={appId}
            environment={environment}
          />
        </div>
      </ChartCard>

      {/* Bottom-left: Model History */}
      <ChartCard>
        <div className="flex items-start justify-between mb-4" style={{ minHeight: '40px' }}>
          <h3 className={`${typography.size.xs} ${typography.weight.semibold} uppercase tracking-wide`} style={{ color: palette.text.neutral }}>
            Model History
          </h3>
          <TokenDropdown
            selectedOption={selectedTimeMetricOption}
            options={METRIC_OPTIONS}
            onSelect={setSelectedTimeMetric}
          />
        </div>
        <div style={{ height: '280px', width: '100%' }}>
          <ModelCostOverTimeChart
            data={modelCostOverTimeData}
            selectedMetric={selectedTimeMetric}
            timeWindow={timeWindow}
            appId={appId}
            environment={environment}
          />
        </div>
      </ChartCard>

      {/* Bottom-right: Agent History */}
      <ChartCard>
        <div className="flex items-start justify-between mb-4" style={{ minHeight: '40px' }}>
          <h3 className={`${typography.size.xs} ${typography.weight.semibold} uppercase tracking-wide`} style={{ color: palette.text.neutral }}>
            Agent History
          </h3>
          <TokenDropdown
            selectedOption={selectedTimeTokenOption}
            options={METRIC_OPTIONS}
            onSelect={setSelectedTimeToken}
          />
        </div>
        <div style={{ height: '280px', width: '100%' }}>
          <AgentCostOverTimeChart
            data={agentCostOverTimeData}
            selectedMetric={selectedTimeToken}
            timeWindow={timeWindow}
            appId={appId}
            environment={environment}
          />
        </div>
      </ChartCard>
    </div>
  );
};

// Memoize component to prevent re-renders when props haven't changed
export default React.memo(CostAnalysisGrid, (prevProps, nextProps) => {
  // Custom comparison: only re-render if these values actually changed
  const modelDataChanged = prevProps.modelCostOverTimeData !== nextProps.modelCostOverTimeData;
  const agentDataChanged = prevProps.agentCostOverTimeData !== nextProps.agentCostOverTimeData;
  const timeWindowChanged = prevProps.timeWindow !== nextProps.timeWindow;
  const appIdChanged = prevProps.appId !== nextProps.appId;
  const environmentChanged = prevProps.environment !== nextProps.environment;

  const shouldNotUpdate = !modelDataChanged && !agentDataChanged && !timeWindowChanged && !appIdChanged && !environmentChanged;

  return shouldNotUpdate;
});
